// @flow

import { observer } from "mobx-react";
import React from "react";
import MediaRecorderStore from "app/stores/MediaRecorderStore";
import Icon from "app/components/Icon/Icon";
import VideoRecorder from "app/components/MediaRecorder/VideoRecorder/VideoRecorder";
import AudioRecorder from "app/components/MediaRecorder/AudioRecorder/AudioRecorder";

import "./MediaRecorder.scss";
import { MEDIA } from "../../stores/MediaRecorderStore";

type Props = {};
type State = {
    error: ?string
};

@observer
export default class MediaRecorder extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            error: null
        };
    }

    handleError = e => {
        this.setState(state => ({ ...state, error: e }));
    };

    handleStartRecording = () => {
        MediaRecorderStore.startRecording();
    };

    handleStopRecording = () => {
        MediaRecorderStore.stopRecording();
    };

    handleRecordingStarted = () => {
        MediaRecorderStore.startedRecording();
    };

    handleRecordingStopped = () => {
        MediaRecorderStore.stoppedRecording();
    };

    handleSelectMedia = (media: string) => {
        if (!MediaRecorderStore.isRecording) {
            MediaRecorderStore.setMedia(media);
        }
    };

    handleRecorded = (file: File) => {
        MediaRecorderStore.setRecordedFile(file);
    };

    handleClose = () => {
        MediaRecorderStore.closeRecorder();
    };

    renderError = () => {
        if (this.state.error) {
            return <div className="alert alert-danger">{this.state.error}</div>;
        }
    };

    renderSelectMedia = () => {
        let selectedMedia = MediaRecorderStore.media;
        let audioClasses = "btn";
        let audioVideoClasses = "btn";
        switch (selectedMedia) {
            case MEDIA.AUDIO_ONLY:
                audioClasses += " active";
                break;
            case MEDIA.AUDIO_AND_VIDEO:
                audioVideoClasses += " active";
                break;
            default:
                throw Error("Unknown media " + MediaRecorderStore.media);
        }

        return (
            <div className="MediaRecorder__Container__SelectMedia">
                <button
                    className={audioClasses}
                    type="button"
                    onClick={this.handleSelectMedia.bind(
                        this,
                        MEDIA.AUDIO_ONLY
                    )}
                >
                    <Icon fontAwesome={true} name="microphone" />
                </button>
                <button
                    className={audioVideoClasses}
                    type="button"
                    onClick={this.handleSelectMedia.bind(
                        this,
                        MEDIA.AUDIO_AND_VIDEO
                    )}
                >
                    <Icon fontAwesome={true} name="video-camera" />
                </button>
            </div>
        );
    };

    renderAudioAndVideo = () => {
        return (
            <VideoRecorder
                key="audio_and_video"
                record={MediaRecorderStore.record}
                onRecorded={this.handleRecorded}
                audio={true}
                onRecordingStarted={this.handleRecordingStarted}
                onRecordingStopped={this.handleRecordingStopped}
                onError={this.handleError}
            />
        );
    };

    renderAudioOnly = () => {
        return (
            <AudioRecorder
                record={MediaRecorderStore.record}
                onRecorded={this.handleRecorded}
                onRecordingStarted={this.handleRecordingStarted}
                onRecordingStopped={this.handleRecordingStopped}
                onError={this.handleError}
            />
        );
    };

    renderVideoOnly = () => {
        return (
            <VideoRecorder
                record={MediaRecorderStore.record}
                key="video_only"
                onRecorded={this.handleRecorded}
                audio={true}
                onRecordingStarted={this.handleRecordingStarted}
                onRecordingStopped={this.handleRecordingStopped}
            />
        );
    };

    renderSelectedMedia = () => {
        switch (MediaRecorderStore.media) {
            case MEDIA.AUDIO_ONLY:
                return this.renderAudioOnly();
            case MEDIA.AUDIO_AND_VIDEO:
                return this.renderAudioAndVideo();
            case MEDIA.VIDEO_ONLY:
                return this.renderVideoOnly();
            default:
                throw Error("Unknown media " + MediaRecorderStore.media);
        }
    };

    renderRecorder = () => {
        let recordingButtonClasses =
            "btn MediaRecorder__Container__Controls__RecordButton";
        if (MediaRecorderStore.isRecording) {
            recordingButtonClasses +=
                " MediaRecorder__Container__Controls__RecordButton--recording";
        }

        return (
            <div className="MediaRecorder__Container">
                <div className="MediaRecorder__Container__TopBar">
                    <div className="pull-right">
                        <button
                            type="button"
                            className="btn MediaRecorder__Container__TopBar__CloseButton"
                            onClick={this.handleClose}
                        >
                            <Icon fontAwesome={true} name="times" />
                        </button>
                    </div>
                    {this.renderSelectMedia()}
                </div>
                <div className="MediaRecorder__Container__Media">
                    {this.renderSelectedMedia()}
                </div>
                <div className="MediaRecorder__Container__Controls">
                    <button
                        className={recordingButtonClasses}
                        type="button"
                        onClick={this.handleStartRecording}
                        disabled={MediaRecorderStore.isRecording}
                    >
                        <Icon fontAwesome={true} name="circle" />
                    </button>
                    <button
                        className="btn MediaRecorder__Container__Controls__StopButton"
                        type="button"
                        onClick={this.handleStopRecording}
                        disabled={!MediaRecorderStore.isRecording}
                    >
                        <Icon fontAwesome={true} name="stop" />
                    </button>
                    {this.renderDuration()}
                    {this.renderProgress()}
                </div>
            </div>
        );
    };

    renderProgress = () => {
        let duration = MediaRecorderStore.duration;
        let durationLimit = MediaRecorderStore.durationLimit;
        let percentage = Math.ceil((duration / durationLimit) * 100);
        return (
            <div className="MediaRecorder__Container__Controls__Progress">
                <div className="MediaRecorder__Container__Controls__Progress__Bg" />
                <div
                    className="MediaRecorder__Container__Controls__Progress__Circle"
                    style={{ left: `${percentage}%` }}
                />
            </div>
        );
    };

    renderDuration = () => {
        let duration = MediaRecorderStore.duration;
        let minutes = Math.floor(duration / 60);
        let seconds = Math.round(duration % 60);
        let secondsText = seconds < 10 ? `0${seconds}` : `${seconds}`;
        let durationLimit = MediaRecorderStore.durationLimit;
        let minutesLimit = Math.floor(durationLimit / 60);
        let secondsLimit = Math.round(durationLimit % 60);
        let secondsLimitText =
            secondsLimit < 10 ? `0${secondsLimit}` : `${secondsLimit}`;

        let durationText = `${minutes}:${secondsText}`;
        let durationLimitText = `${minutesLimit}:${secondsLimitText}`;
        return (
            <span className="MediaRecorder__Container__Controls__Duration">
                <span className="MediaRecorder__Container__Controls__Duration__Duration">
                    {durationText}
                </span>
                <span className="MediaRecorder__Container__Controls__Duration__Separator">
                    /
                </span>
                <span className="MediaRecorder__Container__Controls__Duration__Limit">
                    {durationLimitText}
                </span>
            </span>
        );
    };

    render() {
        return (
            <div className="MediaRecorder">
                <div className="MediaRecorder__overlay" />

                <div
                    className="modal show MediaRecorder__modal"
                    tabIndex="-1"
                    role="dialog"
                >
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-body">
                                {this.renderError()}
                                {this.renderRecorder()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/components/MediaRecorder/MediaRecorder.js